import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  Mail, Phone, MapPin,
  Facebook, Instagram, Twitter, Linkedin,
  ArrowRight, ShieldCheck, CreditCard, Globe2, Award, Star, Users,
} from 'lucide-react';
import { GET_SITE_SETTINGS, SUBSCRIBE_NEWSLETTER } from '../graphql/footer';
import { GET_TOUR_CATEGORIES } from '../graphql/tours';

const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.18)';

/* ─── Icon registry for trust badges ─── */
const ICON_MAP = { ShieldCheck, Award, Globe2, CreditCard, Star, Users };
const getIcon  = (name) => ICON_MAP[name] || ShieldCheck;

/* ─── Social platform → icon + label ─── */
const SOCIAL_MAP = {
  facebookUrl:  { icon: Facebook,  label: 'Facebook'  },
  instagramUrl: { icon: Instagram, label: 'Instagram' },
  twitterUrl:   { icon: Twitter,   label: 'Twitter'   },
  linkedinUrl:  { icon: Linkedin,  label: 'LinkedIn'  },
};

const FooterFontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500;600&display=swap');
    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }
    .footer-link { position:relative; display:inline-block; transition:color .3s; }
    .footer-link::after { content:''; position:absolute; bottom:-1px; left:0; width:0; height:1px; background:#B8975A; transition:width .35s cubic-bezier(.22,1,.36,1); }
    .footer-link:hover::after { width:100%; }
    .footer-link:hover { color:#e2c98a; }
    .nl-input:focus { outline:none; border-color:#B8975A; }
    .nl-input::placeholder { color:#334155; }
    .social-icon { width:38px; height:38px; display:flex; align-items:center; justify-content:center; border:1px solid rgba(255,255,255,0.1); transition:all .35s cubic-bezier(.22,1,.36,1); color:#64748b; }
    .social-icon:hover { border-color:#B8975A; color:#B8975A; transform:translateY(-2px); }
    .trust-badge { display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:28px 16px; border:1px solid rgba(255,255,255,0.04); transition:border-color .35s; }
    .trust-badge:hover { border-color:rgba(184,151,90,0.25); }
    .gold-divider { height:1px; background:linear-gradient(90deg,transparent 0%,rgba(184,151,90,0.3) 40%,rgba(184,151,90,0.3) 60%,transparent 100%); }
    .nl-band { background: radial-gradient(ellipse 60% 80% at 80% 50%,rgba(184,151,90,.06) 0%,transparent 70%), #0a1020; }
    @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    .nl-sent { animation:fadeUp .4s cubic-bezier(.22,1,.36,1) both; }
  `}</style>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email,     setEmail]     = useState('');
  const [nlStatus,  setNlStatus]  = useState('idle'); // idle | loading | success | duplicate | error

  const { data: settingsData } = useQuery(GET_SITE_SETTINGS);
  const { data: catsData }     = useQuery(GET_TOUR_CATEGORIES);
  const [subscribeNewsletter]  = useMutation(SUBSCRIBE_NEWSLETTER);

  const s          = settingsData?.siteSettings;
  const quickLinks = s?.quickLinks    || [];
  const badges     = s?.trustBadges  || [];
  const categories = catsData?.tourCategories || [];

  // Build social links array from DB values, only show ones with a URL set
  const socialLinks = Object.entries(SOCIAL_MAP)
    .map(([key, { icon, label }]) => ({
      icon, label,
      href: s?.[key] || '',
    }))
    .filter(l => l.href);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNlStatus('loading');
    try {
      const { data } = await subscribeNewsletter({ variables: { email } });
      const msg = data?.subscribeNewsletter?.message;
      setNlStatus(msg === 'already_subscribed' ? 'duplicate' : 'success');
    } catch {
      setNlStatus('error');
    }
  };

  const legalLinks = [
    { name: 'Privacy Policy',      path: '/privacy'       },
    { name: 'Terms of Service',    path: '/terms'         },
    { name: 'Cookie Policy',       path: '/cookies'       },
    { name: 'Cancellation Policy', path: '/cancellation'  },
  ];

  return (
    <footer className="f-jost bg-[#080e1a] text-slate-400 dark-sec" style={{ borderTop: `1px solid ${GOLD_DIM}` }}>
      <FooterFontLoader />

      {/* ── Newsletter band ── */}
      <div className="nl-band border-b" style={{ borderColor: GOLD_DIM }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-16 md:py-20">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
            <div className="max-w-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px" style={{ background: GOLD }} />
                <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Inner Circle</span>
              </div>
              <h3 className="f-cormorant text-white text-[38px] sm:text-[38px] font-light leading-[1.1] mb-3">
                Journeys before<br />they go public.
              </h3>
              <p className="f-jost text-slate-400 text-[15px] font-light leading-relaxed">
                {s?.newsletterTagline || 'Migration updates, new safaris and seasonal tips from our team on the ground — a few times a year.'}
              </p>
            </div>

            <div className="w-full max-w-md">
              {nlStatus === 'success' ? (
                <div className="nl-sent flex items-start gap-4 py-4">
                  <div className="w-8 h-px mt-3 shrink-0" style={{ background: GOLD }} />
                  <div>
                    <p className="f-cormorant text-white text-2xl leading-snug mb-1">You're on the list.</p>
                    <p className="f-jost text-slate-500 text-[15px]">Expect your first dispatch within the week.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleNewsletter}>
                  <div className="flex items-stretch border transition-colors duration-300" style={{ borderColor: GOLD_DIM }}>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Your email address"
                      className="nl-input flex-1 min-w-0 bg-transparent px-5 py-4 text-white text-[15px] f-jost tracking-wide border-0"
                      required
                    />
                    <button
                      type="submit"
                      disabled={nlStatus === 'loading'}
                      className="px-7 py-4 f-jost text-[13px] font-semibold uppercase tracking-[0.16em] transition-all duration-300 shrink-0 flex items-center gap-2"
                      style={{ background: GOLD, color: '#0a0e1a', opacity: nlStatus === 'loading' ? 0.7 : 1 }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#cead72'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = GOLD; }}
                    >
                      {nlStatus === 'loading' ? 'Sending…' : <><span>Subscribe</span> <ArrowRight size={11} /></>}
                    </button>
                  </div>
                  {nlStatus === 'duplicate' && (
                    <p className="f-jost text-[13px] mt-2" style={{ color: GOLD }}>
                      You're already subscribed — we'll see you in the next dispatch.
                    </p>
                  )}
                  {nlStatus === 'error' && (
                    <p className="f-jost text-[13px] text-red-400 mt-2">
                      Something went wrong. Please try again.
                    </p>
                  )}
                  {nlStatus === 'idle' && (
                    <p className="f-jost text-[13px] text-slate-600 mt-3 tracking-wide">No spam. Unsubscribe at any time.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="py-16 md:py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">

          {/* Brand column */}
          <div className="sm:col-span-2 lg:col-span-4 pr-0 lg:pr-6">
            <Link to="/" className="inline-flex items-center gap-4 mb-7 group" style={{ textDecoration: 'none' }}>
              <img className='w-20' src="/aio.svg" alt="aio logo" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1 }}>
                <span className="f-cormorant" style={{ fontSize: '27px', fontWeight: 300, letterSpacing: '.16em', color: '#fff' }}>VOLE LUXE</span>
                <span className="f-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD }}>Travel & Safaris</span>
              </div>
            </Link>

            <p className="f-cormorant text-[22px] text-slate-300 leading-relaxed mb-3 font-light">
              "{s?.brandStatement || 'Crafting profound encounters between curious souls and the world\'s great wildernesses.'}"
            </p>
            <p className="f-jost text-[15px] text-slate-600 leading-relaxed mb-8 font-light">
              {s?.establishedLabel || 'Tailor-made safaris · East Africa'}
            </p>

            {socialLinks.length > 0 && (
              <div className="flex gap-2">
                {socialLinks.map(({ icon: Icon, label, href }) => (
                  <a key={label} href={href} aria-label={label} className="social-icon">
                    <Icon size={15} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-4 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] text-slate-400">Navigate</span>
            </div>
            <ul className="space-y-4">
              {quickLinks.map(({ id, label, path }) => (
                <li key={id}>
                  <Link to={path} className="footer-link f-jost text-[15px] text-slate-400 tracking-wide">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Tour categories from DB */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-4 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] text-slate-400">Experiences</span>
            </div>
            <ul className="space-y-4">
              {categories.map(({ id, name, slug }) => (
                <li key={id}>
                  <Link
                    to={`/tours?category=${slug}`}
                    className="footer-link f-jost text-[15px] text-slate-400 tracking-wide"
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact from Agency model */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-4 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] text-slate-400">Headquarters</span>
            </div>
            <div className="space-y-5">
              {s?.address && (
                <div className="flex items-start gap-4">
                  <MapPin size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" style={{ color: GOLD }} />
                  <p className="f-jost text-[15px] text-slate-400 leading-relaxed font-light">
                    {s.address}{s.city ? `, ${s.city}` : ''}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-5">
                {s?.primaryEmail && (
                  <a href={`mailto:${s.primaryEmail}`} className="footer-link f-jost text-[15px] text-slate-400" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Mail size={14} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                    {s.primaryEmail}
                  </a>
                )}
                {s?.phone && (
                  <a href={`tel:${s.phone}`} className="footer-link f-jost text-[15px] text-slate-400" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Phone size={14} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                    {s.phone}
                  </a>
                )}
              </div>
              <div className="pt-3">
                <Link to="/contact" className="inline-flex items-center gap-3 f-jost text-[13px] font-semibold uppercase tracking-[0.16em] group transition-colors" style={{ color: GOLD }}>
                  Speak to a specialist
                  <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="gold-divider" />
        <div className="py-2">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {badges.map(({ id, icon, label, sub }) => {
              const Icon = getIcon(icon);
              return (
                <div key={id} className="trust-badge">
                  <Icon size={20} strokeWidth={1.5} style={{ color: GOLD }} />
                  <div>
                    <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] text-slate-300 mb-1">{label}</p>
                    <p className="f-jost text-[13px] text-slate-500 tracking-wide">{sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="gold-divider" />

        {/* Bottom bar */}
        <div className="py-8 flex flex-col md:flex-row justify-between items-center gap-5">
          <div className="flex items-center gap-4">
            <div className="w-4 h-px hidden sm:block" style={{ background: GOLD }} />
            <p className="f-jost text-[11px] sm:text-[13px] text-slate-500 uppercase tracking-[0.12em] sm:tracking-[0.16em] text-center">
              © {currentYear} Vole Luxe Travel & Safaris. All rights reserved.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:gap-x-6">
            {legalLinks.map(({ name, path }, i) => (
              <React.Fragment key={name}>
                <Link to={path} className="footer-link f-jost text-[11px] sm:text-[13px] text-slate-500 uppercase tracking-[0.1em] sm:tracking-[0.14em] whitespace-nowrap">{name}</Link>
                {i < legalLinks.length - 1 && <span className="w-px h-3 bg-slate-700 hidden sm:inline-block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;